// Pure geometry / GLA helpers for the Tape (feet-based freehand measurement) tool.
// Ported from JMI-GeoSketch-Standalone's src/lib/sketch-types.ts.
// Feature "type" is a config-driven id (see GET tapesketch/config) rather than a fixed enum,
// so type-specific logic dispatches on the feature type's `code` (seeded: DWL/GAR/POR/BAL/DK/PAT/PH/PAV/OTH)
// rather than a hardcoded string union.

// Tape mode sizes itself off the viewport rather than the Aerial section's box model
// (that section is expected to be replaced). The draw pad below the canvas keeps its
// own natural/static height; this is just a floor so the canvas never gets too cramped.
export const TAPE_PANEL_MIN_HEIGHT = 480;

export const STORY_OPTIONS = [1, 1.5, 2, 2.5, 3];
export const STORY_ABOVE_OPTIONS = [0, 0.5, 1, 1.5, 2, 2.5, 3];

export const GARAGE_KIND_DEFAULT_ABOVE = {
  Standard: 0,
  'Built-in': 1,
  Half: 0.5,
  Basement: 1,
};

export const TYPE_COLORS = {
  DWL: '#c9b87a',
  GAR: '#6a92b3',
  POR: '#c48a62',
  BAL: '#b89a6a',
  DK: '#6a9e74',
  PAT: '#8a8f7a',
  PH: '#8e7aa8',
  PAV: '#5eae9a',
  OTH: '#9aa3ad',
};

export const SHEET_FILL_COLORS = {
  DWL: '#efe6c8',
  GAR: '#d5e3ee',
  POR: '#edd4c0',
  BAL: '#e6d5b8',
  DK: '#d5e8d8',
  PAT: '#e2e2d6',
  PH: '#e3d8ee',
  PAV: '#d4ece6',
  OTH: '#e4e4e4',
};

export function uid() {
  return Math.random().toString(36).slice(2, 9);
}

export function getFeatureTypeCode(tapeFeatureTypeId, tapeConfig) {
  const t = tapeConfig?.featureTypes?.find(x => x.id === tapeFeatureTypeId);
  return t ? t.code : 'OTH';
}

export function getFeatureTypeName(tapeFeatureTypeId, tapeConfig) {
  const t = tapeConfig?.featureTypes?.find(x => x.id === tapeFeatureTypeId);
  return t ? t.name : 'Other';
}

export function getFoundationCode(tapeFoundationTypeId, tapeConfig) {
  const t = tapeConfig?.foundationTypes?.find(x => x.id === tapeFoundationTypeId);
  return t ? t.code : '';
}

export function getGarageKindName(tapeGarageKindId, tapeConfig) {
  const t = tapeConfig?.garageKinds?.find(x => x.id === tapeGarageKindId);
  return t ? t.name : 'Standard';
}

function shoelace(pts) {
  if (!pts || pts.length < 3) {
    return 0;
  }
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

export function measureSqft(vertices) {
  return shoelace(vertices);
}

export function polygonCentroid(pts) {
  if (!pts || !pts.length) {
    return { x: 0, y: 0 };
  }
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    const c = p.x * q.y - q.x * p.y;
    a += c;
    cx += (p.x + q.x) * c;
    cy += (p.y + q.y) * c;
  }
  a *= 0.5;
  if (Math.abs(a) < 1e-8) {
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
      y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
    };
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

export function pointInPoly(pt, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    const hit = (a.y > pt.y) !== (b.y > pt.y) && pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y || 1e-15) + a.x;
    if (hit) {
      inside = !inside;
    }
  }
  return inside;
}

function distToSeg(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const l2 = vx * vx + vy * vy || 1e-12;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2));
  return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}

function distToEdges(p, pts) {
  let d = Infinity;
  for (let i = 0; i < pts.length; i++) {
    d = Math.min(d, distToSeg(p, pts[i], pts[(i + 1) % pts.length]));
  }
  return d;
}

/** Visual center guaranteed to sit inside the polygon (handles U-shapes). */
export function labelAnchor(pts, avoid = []) {
  if (pts.length < 3) {
    return polygonCentroid(pts);
  }
  let minx = Infinity;
  let miny = Infinity;
  let maxx = -Infinity;
  let maxy = -Infinity;
  for (const p of pts) {
    minx = Math.min(minx, p.x);
    miny = Math.min(miny, p.y);
    maxx = Math.max(maxx, p.x);
    maxy = Math.max(maxy, p.y);
  }
  const blocked = p => avoid.some(ring => ring.length > 2 && pointInPoly(p, ring));
  const score = p => {
    if (!pointInPoly(p, pts) || blocked(p)) {
      return -1;
    }
    return distToEdges(p, pts);
  };
  const c = polygonCentroid(pts);
  let best = c;
  let bestD = score(c);
  const n = 32;
  const w = maxx - minx;
  const h = maxy - miny;
  for (let iy = 1; iy < n; iy++) {
    for (let ix = 1; ix < n; ix++) {
      const p = { x: minx + (ix / n) * w, y: miny + (iy / n) * h };
      const d = score(p);
      if (d > bestD) {
        bestD = d;
        best = p;
      }
    }
  }
  if (bestD < 0) {
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
      y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
    };
  }
  return best;
}

export function edgeLengths(vertices) {
  const out = [];
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    out.push(Math.hypot(b.x - a.x, b.y - a.y));
  }
  return out;
}

export function garageStoriesAbove(feature, garageKindName) {
  if (garageKindName === 'Half') {
    return 0.5;
  }
  if (feature.storiesAbove != null) {
    return feature.storiesAbove;
  }
  return GARAGE_KIND_DEFAULT_ABOVE[garageKindName] ?? 0;
}

export function livingStoriesAbove(feature, typeCode, garageKindName) {
  if (typeCode === 'GAR') {
    return garageStoriesAbove(feature, garageKindName);
  }
  if (typeCode === 'POR' || typeCode === 'BAL') {
    return feature.storiesAbove ?? 0;
  }
  return 0;
}

export function livingSqft(feature, typeCode, garageKindName) {
  const fp = measureSqft(feature.vertices);
  if (typeCode === 'DWL') {
    return fp * (feature.isCathedral ? 1 : feature.stories);
  }
  if (typeCode === 'GAR' || typeCode === 'POR' || typeCode === 'BAL') {
    return fp * livingStoriesAbove(feature, typeCode, garageKindName);
  }
  return 0;
}

function fmtStoryCode(n) {
  if (n === 0.5) {
    return '.5';
  }
  return String(n);
}

/** Section stamp: story first when present. 1/B  1/AG  1.5/OP — no story → AG, OP, BAL */
export function sectionAbbrev(feature, typeCode, foundationCode, garageKindName) {
  if (typeCode === 'DWL') {
    if (feature.isCathedral) {
      return `CATH/${foundationCode}`;
    }
    return `${fmtStoryCode(feature.stories)}/${foundationCode}`;
  }
  if (typeCode === 'GAR') {
    const code = garageKindName === 'Built-in' ? 'BIG' : garageKindName === 'Basement' ? 'BsG' : 'AG';
    const above = garageStoriesAbove(feature, garageKindName);
    return above ? `${fmtStoryCode(above)}/${code}` : code;
  }
  if (typeCode === 'POR') {
    const a = feature.storiesAbove ?? 0;
    return a ? `${fmtStoryCode(a)}/OP` : 'OP';
  }
  if (typeCode === 'BAL') {
    const a = feature.storiesAbove ?? 0;
    return a ? `${fmtStoryCode(a)}/BAL` : 'BAL';
  }
  return typeCode || 'OTH';
}

// ---- Draw-canvas geometry helpers (pan/draw/edit) ----

export function step(p, dir, ft) {
  if (dir === 'N') {
    return { x: p.x, y: p.y + ft };
  }
  if (dir === 'S') {
    return { x: p.x, y: p.y - ft };
  }
  if (dir === 'E') {
    return { x: p.x + ft, y: p.y };
  }
  return { x: p.x - ft, y: p.y };
}

export function orthoSnap(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { x: to.x, y: from.y };
  }
  return { x: from.x, y: to.y };
}

export function roundPt(p) {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}

export function projectOnSeg(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy || 1;
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

export { distToSeg };

export function stretchEdge(orig, index, start, raw) {
  const n = orig.length;
  const a = orig[index];
  const b = orig[(index + 1) % n];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const tx = dx / len;
  const ty = dy / len;
  const nx = -ty;
  const ny = tx;
  const mx = raw.x - start.x;
  const my = raw.y - start.y;
  const along = mx * tx + my * ty;
  const perp = mx * nx + my * ny;
  const next = orig.map(p => ({ ...p }));
  if (Math.abs(along) >= Math.abs(perp)) {
    const d = Math.round(along);
    if (d === 0) {
      return orig;
    }
    if (d > 0) {
      const i1 = (index + 1) % n;
      const i2 = (index + 2) % n;
      next[i1] = roundPt({ x: orig[i1].x + tx * d, y: orig[i1].y + ty * d });
      next[i2] = roundPt({ x: orig[i2].x + tx * d, y: orig[i2].y + ty * d });
    } else {
      const i0 = index;
      const iN = (index - 1 + n) % n;
      next[i0] = roundPt({ x: orig[i0].x + tx * d, y: orig[i0].y + ty * d });
      next[iN] = roundPt({ x: orig[iN].x + tx * d, y: orig[iN].y + ty * d });
    }
  } else {
    const d = Math.round(perp);
    if (d === 0) {
      return orig;
    }
    next[index] = roundPt({ x: a.x + nx * d, y: a.y + ny * d });
    next[(index + 1) % n] = roundPt({ x: b.x + nx * d, y: b.y + ny * d });
  }
  return next;
}

export function hitFeature(features, screen, raw, pxPerFt, selectedId) {
  const ordered = [...features].reverse();
  const sel = ordered.find(f => f.id === selectedId);
  const rest = ordered.filter(f => f.id !== selectedId);
  for (const f of sel ? [sel, ...rest] : rest) {
    const pts = f.vertices || [];
    const thresh = 12 / pxPerFt;
    for (let i = 0; i < pts.length; i++) {
      if (Math.hypot(raw.x - pts[i].x, raw.y - pts[i].y) <= thresh) {
        return { id: f.id, kind: 'vertex', index: i, snap: pts[i] };
      }
    }
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      if (distToSeg(raw, a, b) <= thresh) {
        return { id: f.id, kind: 'edge', index: i, snap: roundPt(projectOnSeg(raw, a, b)) };
      }
    }
    if (pointInPoly(raw, pts)) {
      return { id: f.id, kind: 'body', index: 0, snap: roundPt(raw) };
    }
  }
  return null;
}

export function snapToExisting(features, raw, pxPerFt) {
  const thresh = Math.max(1, 14 / pxPerFt);
  let best = null;
  let bestD = thresh;
  for (const f of features) {
    const pts = f.vertices || [];
    for (const p of pts) {
      const d = Math.hypot(raw.x - p.x, raw.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      const proj = projectOnSeg(raw, a, b);
      const d = Math.hypot(raw.x - proj.x, raw.y - proj.y);
      if (d < bestD) {
        bestD = d;
        best = roundPt(proj);
      }
    }
  }
  return best;
}
